import { useSelector } from 'react-redux';
import { Button } from '@mui/material';

import { useAssets } from '@labradorsports/assets';
import { CommunityPlaybookSections } from '@labradorsports/constants';
import { createGraphMetadata, useSite } from '@labradorsports/utils';

import { HelmetWrapper, InlineLoadingSpinner } from '../../shared/index.js';
import { RouterSelectors, useGetCommunityPlaybookQuery } from '../../store/index.js';
import CommunityPagination from '../community-pagination/community-pagination.js';
import CommunityPlayList from '../community-play-list/community-play-list.js';

const CommunitySectionTitles = {
    [CommunityPlaybookSections.LATEST]: 'Latest',
    [CommunityPlaybookSections.POPULAR]: 'Popular',
    [CommunityPlaybookSections.TRENDING]: 'Trending',
};

interface Props {
    addPlay: AnyFunction;
    section: string;
}

const CommunitySectionPage: FC<Props> = ({ addPlay, section }) => {
    const { LogoImg, AddFrameWhiteIcon } = useAssets();
    const { Config } = useSite();
    const pageNumber = useSelector(RouterSelectors.communityPageNumber);
    const { data: { plays, estimatedPages } = {}, isLoading } = useGetCommunityPlaybookQuery({
        section,
        page: pageNumber,
    });

    if (!CommunityPlaybookSections[section]) {
        return null;
    }

    if (isLoading) {
        return <InlineLoadingSpinner />;
    }

    const description = {
        [CommunityPlaybookSections.LATEST]: `Browse the latest animated play diagrams from ${Config.SiteConfig.SiteName} users and experts.`,
        [CommunityPlaybookSections.POPULAR]: `Browse the most popular play diagrams from ${Config.SiteConfig.SiteName} users and experts.`,
        [CommunityPlaybookSections.TRENDING]: `Browse the animated plays that ${Config.SiteConfig.SiteName} users are finding the most helpful recently.`,
    }[section];

    const title = CommunitySectionTitles[section];

    const canonical = `${Config.SiteConfig.Url}/community/${section.toLowerCase()}?pg=${pageNumber}`;

    const breadcrumbs = [
        {
            name: Config.SiteConfig.SiteName,
            url: Config.SiteConfig.Url,
        },
        {
            name: 'Community Playbook',
            url: `${Config.SiteConfig.Url}/community`,
        },
        {
            name: title,
            url: `${Config.SiteConfig.Url}/${section.toLowerCase()}`,
        },
    ];

    return (
        <div className="community-section-page p-3">
            <HelmetWrapper title={`${title} Community Playbook`} description={description} image={LogoImg}>
                <link rel="canonical" href={canonical} />
                <meta property="og:url" content={canonical} />

                <script type="application/ld+json">{createGraphMetadata(breadcrumbs)}</script>
            </HelmetWrapper>
            <div className="d-flex">
                <h3 className="flex-grow-1">{title}</h3>
                <div className="flex-shrink-0">
                    <Button variant="contained" color="secondary" className="rounded-button" onClick={addPlay}>
                        <img src={AddFrameWhiteIcon} alt="Add icon" />
                        Add Plays
                    </Button>
                </div>
            </div>

            {plays ? <CommunityPagination numPages={estimatedPages} useHref /> : null}
            <CommunityPlayList plays={plays} />
            {plays ? <CommunityPagination numPages={estimatedPages} useHref /> : null}
        </div>
    );
};

export default CommunitySectionPage;
